/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Messages Schema", description = "Schema information and diagrams for Hightouch's Campaign Intelligence for email and SMS messages.", lastUpdatedDate = new Date(1733184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Messages Schema",
        description: "Schema information and diagrams for Hightouch's Campaign Intelligence for email and SMS messages.",
        lastUpdatedDate: "Tue Dec 03 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Schema",
          "depth": 0,
          "id": "schema"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      ol: "ol",
      li: "li",
      strong: "strong",
      img: "img",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Campaign Intelligence is available as an add-on on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier\nplans"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Campaign Intelligence for ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/campaign-intelligence/emails",
          children: "messages"
        }), ", such as emails and SMS, leverages three types of models:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Users"
          }), " are the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/customer-studio/schema#parent-models",
            children: "parent model"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://hightouch.com/docs/campaign-intelligence/events",
            children: _jsx(_components.strong, {
              children: "Events"
            })
          }), " are events that connect users to an asset, such as email clicks or opens."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Emails"
          }), " are a type of ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/campaign-intelligence/assets",
            children: "asset"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "schema",
        children: "Schema"
      }), "\n", _jsx(_components.p, {
        children: "In the entity relationship diagram (ERD) below, we show a schema for messages using emails as an example. The ERD shows expected model columns and their relationships, with necessary primary (PK) and foreign keys (FK)."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/schema-messages.png",
          alt: "Messages schema"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In the ERD above, Users are connected to different ", _jsx(_components.strong, {
          children: "Events"
        }), " -- such as email opens and clicks -- through a unique ", _jsx(_components.code, {
          children: "user_id"
        }), ". ", _jsx(_components.strong, {
          children: "Events"
        }), " require a ", _jsx(_components.code, {
          children: "timestamp"
        }), ". They can have additional columns based on the ", _jsx(_components.strong, {
          children: "Event type"
        }), ", such as a ", _jsx(_components.code, {
          children: "clicked_url"
        }), " for email click events."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each ", _jsx(_components.strong, {
          children: "Event"
        }), " is also linked to an ", _jsx(_components.strong, {
          children: "Asset model"
        }), ", either email or SMS, by way of a ", _jsx(_components.code, {
          children: "message_id"
        }), ". The message can have a variety of columns, including date columns such as ", _jsx(_components.code, {
          children: "start_at"
        }), " and ", _jsx(_components.code, {
          children: "ended_at"
        }), " to indicate the dates that email asset was in use."]
      })]
    });
  }
}
export default MDXContent;
